import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';

import { USStateObject } from '../../../constants/USstates/constants';
import { Button } from '../../Clickables/Buttons/Button';
import { CloseIcon } from '../../Icons/CloseIcon';
import { SearchIcon } from '../../Icons/SearchIcon';
import { Text } from '../../Text/Text';

import { SearchStateFromList } from './SearchStateFromList';
import { useOnClickOutside } from '../../../helpers/hooks/useOnClickOutside';

interface IProps {
  setSelectedState: (result: USStateObject) => void;
}

export const SearchUSStateFromList = ({ setSelectedState }: IProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [showResults, setShowResults] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [resultsList, setResultsList] = useState<USStateObject[] | undefined>(undefined);

  useEffect(() => {
    if (searchValue) {
      setShowResults(true);
      setResultsList(SearchStateFromList(searchValue));
    }
  }, [searchValue]);

  const onChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  }, []);

  const onFocus = useCallback(() => {
    setShowResults(true);
  }, [setShowResults]);

  const onClickOutside = useCallback(() => {
    if (showResults) {
      setShowResults(false);
      setResultsList(undefined);
    }
  }, [showResults]);

  useOnClickOutside(containerRef, onClickOutside);

  return (
    <div ref={containerRef} className="w-11/12 rounded-md border-2 border-twilight bg-sea-foam">
      <div className="flex w-full justify-between p-4">
        <div className="flex w-full">
          <label htmlFor="SearchBox" className="mr-3">
            <SearchIcon />
          </label>
          <input
            ref={searchInputRef}
            className="w-full text-trench"
            type="text"
            autoComplete="off"
            id="SearchBox"
            data-testid="SearchBox"
            aria-label="Search"
            placeholder="Search for US State..."
            value={searchValue}
            onChange={onChange}
            onFocus={onFocus}
          />
        </div>

        {showResults && (
          <button
            type="button"
            onClick={() => {
              setShowResults(false);
              setResultsList(undefined);
              setSearchValue('');
            }}
            className="mr-0"
          >
            <CloseIcon size={20} />
          </button>
        )}
      </div>
      {showResults && resultsList && resultsList.length > 0 && (
        <div className="relative w-full">
          <div className="absolute flex w-full flex-col items-start rounded-md border-2 border-twilight bg-sea-foam p-2">
            {resultsList.map(result => (
              <Button
                variant="textPrimary"
                onClick={() => {
                  setSelectedState(result);
                  setShowResults(false);
                  setResultsList(undefined);
                  setSearchValue('');
                }}
                className="py-1"
                key={`SearchUSStateFromList-${result.stateID}`}
              >
                {result.stateName}
              </Button>
            ))}
          </div>
        </div>
      )}
      {showResults && resultsList && resultsList.length === 0 && (
        <Text className="mb-0 p-4 text-midnight">No results</Text>
      )}
    </div>
  );
};
